/**
 * Represents a shift, which is characterized by a number of weeks,
 * a list of days of the week and a recurrence, as well as a name and
 * a time zone.
 *
 * @class Shift
 * @property {string} id - ID of the shift
 * @property {number} weekCount - Number of weeks that the shift is repeated
 * @property {number[]} daysOfWeek - List of days of the week that the shift
 *                                    takes place (0 = Sunday, 1 = Monday,
 *                                    ... 6 = Saturday)
 * @property {{weeks: {week: number, days: {day: number, startTime: string, endTime: string, breakTime: number}[], totalHours: number }[], totalShiftHours: number}} recurrence - Recurrence of the shift
 * @property {string} timeZone - Time zone of the shift
 * @property {string} name - Name of the shift
 * @property {string} regulationName - Name of the schedule rule that the shift
 *                                     adheres to
 * @property {string} type - Type of shift, can be 'fixed', 'part-time',
 *                           'shiftWork', 'cycle' or 'biweekly'
 *
 * Note: totalShiftHours is in minutes
 */

import { SHIFT_STATUS } from '@src/packages/shifts/constants';

class Shift {
  constructor(
    id,
    weekCount,
    daysOfWeek,
    recurrence,
    timeZone,
    name,
    regulationName,
    type,
    orgId,
    status = SHIFT_STATUS.ACTIVE,
  ) {
    this.id = id;
    this.weekCount = weekCount;
    this.daysOfWeek = daysOfWeek;
    this.recurrence = recurrence;
    this.timeZone = timeZone;
    this.name = name;
    this.regulationName = regulationName;
    this.type = type;
    this.organizationId = orgId;
    this.status = status;
  }

  // Method to validate the configuration
  validate() {
    if (typeof this.weekCount !== 'number' || this.weekCount <= 0) {
      throw new Error('weekCount must be a positive number');
    }
    if (!Array.isArray(this.daysOfWeek) || !this.daysOfWeek.every(day => typeof day === 'number')) {
      throw new Error('daysOfWeek must be an array of numbers');
    }
    if (!this.recurrence) {
      throw new Error('recurrence is required');
    }
    if (typeof this.timeZone !== 'string') {
      throw new Error('timeZone must be a string');
    }
    if (typeof this.name !== 'string') {
      throw new Error('name must be a string');
    }
    if (typeof this.regulationName !== 'string') {
      throw new Error('regulationName must be a string');
    }
    if (typeof this.type !== 'string') {
      throw new Error('type must be a string');
    }
  }

  toUpdate() {
    return {
      id: this.id,
      daysOfWeek: this.daysOfWeek,
      recurrence: this.recurrence,
      timeZone: this.timeZone,
      name: this.name,
      regulationName: this.regulationName,
      status: this.status,
    };
  }
}

export default Shift;
